import { Bell, CalendarClock, Gamepad2, ShieldCheck, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from './api';
import { HexAvatar, Logo } from './components';
import { formatRelative } from './format';
import { Link, RouterProvider, useRouter } from './router';
import { AppProvider, useApp } from './store';
import type { AppNotification } from './types';
import { AdminView } from './views/AdminView';
import { AuthPage, useOAuthResultToast } from './views/AuthPage';
import { GuildDetail } from './views/GuildDetail';
import { Guilds } from './views/Guilds';
import { Home } from './views/Home';
import { ResetPassword, VerifyEmail } from './views/Recovery';
import { NewSession } from './views/NewSession';
import { PlayerDetail } from './views/PlayerDetail';
import { Players } from './views/Players';
import { ProfileView } from './views/ProfileView';
import { AccountPage, NotificationsPage } from './views/Settings';
import { SessionDetail } from './views/SessionDetail';
import { Sessions } from './views/Sessions';

function NotificationsPanel({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const { navigate } = useRouter();

  useEffect(() => {
    api.notifications()
      .then((data) => {
        setItems(data.notifications);
        void api.markNotificationsRead();
      })
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="notif-overlay" onClick={onClose}>
      <div className="notif-panel" role="dialog" aria-label="Notifications" onClick={(event) => event.stopPropagation()}>
      <div className="section-heading slim"><h2>Notifications</h2><button className="ghost small" onClick={onClose}>Close</button></div>
      {items === null ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">Nothing yet. Join or host a session to get things moving.</p>
      ) : (
        items.map((item) => (
          <button
            key={item.id}
            className={`notif-row ${item.read ? '' : 'unread'}`}
            onClick={() => {
              onClose();
              if (item.link) navigate(item.link);
            }}
          >
            <strong>{item.title}</strong>
            <span>{item.body}</span>
            <span className="mono muted">{formatRelative(item.createdAt)}</span>
          </button>
        ))
      )}
      </div>
    </div>
  );
}

function Shell() {
  const { path } = useRouter();
  const { authStatus, profile, toasts } = useApp();
  const [showNotifications, setShowNotifications] = useState(false);
  const [unread, setUnread] = useState(0);
  useOAuthResultToast();

  useEffect(() => {
    if (authStatus !== 'authed') return;
    api.me().then((me) => setUnread(me.unreadNotifications)).catch(() => undefined);
  }, [authStatus, path]);

  const segments = path.replace(/\/+$/, '').split('/').filter(Boolean);

  useEffect(() => {
    const titles: Record<string, string> = {
      sessions: 'Sessions',
      players: 'Players',
      guilds: 'Guilds',
      profile: 'Your profile',
      settings: 'Settings',
      signup: 'Create account',
      login: 'Sign in',
      'reset-password': 'Reset password',
      'verify-email': 'Verify email',
      contact: 'Contact',
      privacy: 'Privacy',
      terms: 'Terms'
    };
    const section = segments[0] ? titles[segments[0]] : null;
    document.title = section ? `${section} · TeamCraft` : 'TeamCraft — Find teammates worth playing with again';
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  let content: React.ReactNode;
  if (segments[0] === 'signup') content = <AuthPage initialMode="signup" />;
  else if (segments[0] === 'login') content = <AuthPage initialMode="login" />;
  else if (segments[0] === 'reset-password') content = <ResetPassword />;
  else if (segments[0] === 'verify-email') content = <VerifyEmail />;
  else if (segments[0] === 'sessions' && segments[1] === 'new') content = <NewSession />;
  else if (segments[0] === 'sessions' && segments[1]) content = <SessionDetail id={segments[1]} />;
  else if (segments[0] === 'sessions') content = <Sessions />;
  else if (segments[0] === 'players' && segments[1]) content = <PlayerDetail id={decodeURIComponent(segments[1])} />;
  else if (segments[0] === 'players') content = <Players />;
  else if (segments[0] === 'guilds' && segments[1]) content = <GuildDetail slug={decodeURIComponent(segments[1])} />;
  else if (segments[0] === 'guilds') content = <Guilds />;
  else if (segments[0] === 'profile') content = authStatus === 'guest' ? <AuthPage initialMode="login" /> : <ProfileView />;
  else if (segments[0] === 'settings' && segments[1] === 'notifications') content = <NotificationsPage />;
  else if (segments[0] === 'settings' && segments[1] === 'account') content = <AccountPage />;
  else if (segments[0] === 'settings') content = authStatus === 'guest' ? <AuthPage initialMode="login" /> : <ProfileView />;
  else if (segments[0] === 'admin') content = <AdminView />;
  else if (segments[0] === 'contact' || segments[0] === 'privacy' || segments[0] === 'terms') content = <StaticPage type={segments[0]} />;
  else if (segments.length === 0) content = <Home />;
  else content = <NotFound />;

  const isAuthRoute = segments[0] === 'signup' || segments[0] === 'login' || segments[0] === 'reset-password' || segments[0] === 'verify-email';
  if (isAuthRoute) {
    return (
      <>
        {content}
        <ToastStack toasts={toasts} />
      </>
    );
  }

  const navItems = [
    { to: '/', label: 'Home', icon: <Gamepad2 size={17} />, active: segments.length === 0 },
    { to: '/sessions', label: 'Sessions', icon: <CalendarClock size={17} />, active: segments[0] === 'sessions' },
    { to: '/players', label: 'Community', icon: <Users size={17} />, active: segments[0] === 'players' || segments[0] === 'guilds' },
    { to: '/profile', label: 'Profile', icon: <ShieldCheck size={17} />, active: segments[0] === 'profile' }
  ];

  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/" className="topbar-logo" aria-label="TeamCraft home"><Logo /></Link>
        <nav className="topbar-nav" aria-label="Primary">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} className={item.active ? 'nav-link active' : 'nav-link'}>
              {item.icon}<span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="topbar-side">
          {authStatus === 'authed' ? (
            <>
              <button className="icon-button" aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`} onClick={() => setShowNotifications((current) => !current)}>
                <Bell size={17} />
                {unread > 0 && <span className="unread-dot" aria-hidden="true" />}
              </button>
              {profile && (
                <Link to="/profile" aria-label="Your profile">
                  <HexAvatar label={profile.avatar} size="sm" />
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to="/login" className="ghost small">Sign in</Link>
              <Link to="/signup" className="primary small">Create account</Link>
            </>
          )}
        </div>
      </header>

      {showNotifications && <NotificationsPanel onClose={() => { setShowNotifications(false); setUnread(0); }} />}

      <main className="workspace">{content}</main>

      <footer className="footer">
        <span>TeamCraft — find teammates worth playing with again.</span>
        <span className="footer-links">
          <Link to="/contact">Contact</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </span>
      </footer>

      <ToastStack toasts={toasts} />
    </div>
  );
}

function ToastStack({ toasts }: { toasts: Array<{ id: number; message: string; tone: string }> }) {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-stack" aria-live="polite">
      {toasts.map((toast) => <div key={toast.id} className={`toast ${toast.tone}`}>{toast.message}</div>)}
    </div>
  );
}

function NotFound() {
  return (
    <div className="page narrow">
      <h1>Page not found</h1>
      <p>That link does not go anywhere. Your squad is probably on the sessions board.</p>
      <div className="action-bar">
        <Link to="/" className="primary small">Go home</Link>
        <Link to="/sessions" className="secondary small">Browse sessions</Link>
      </div>
    </div>
  );
}

function StaticPage({ type }: { type: 'contact' | 'privacy' | 'terms' }) {
  const copy = {
    contact: ['Contact TeamCraft', 'TeamCraft is in early development. Support and feedback are handled directly by the project team for now.'],
    privacy: ['Privacy', 'TeamCraft is designed around player control. Linked platform accounts are optional, Discord is never required, and future data imports will use minimal scopes.'],
    terms: ['Terms', 'TeamCraft is in alpha. Expect changes while the core experience settles; do not rely on it for safety-critical coordination yet.']
  }[type];
  return (
    <div className="page narrow">
      <h1>{copy[0]}</h1>
      <p>{copy[1]}</p>
      <Link to="/" className="secondary small">Back to app</Link>
    </div>
  );
}

export function App() {
  return (
    <RouterProvider>
      <AppProvider>
        <Shell />
      </AppProvider>
    </RouterProvider>
  );
}
